import React, { useState } from 'react'
import { format } from 'date-fns'
import fileDownload from 'js-file-download'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'

import { ROLE, useAuth } from '../../../../../auth'
import { IsGranted } from '../../../../../auth/PrivateRoute'

import { exportOverpaymentList } from '../../core/api'
import { OverpaymentFilters } from '../../core/types'

const AppSwal = withReactContent(Swal)

/** Выгрузка доступна тем же ролям, что и журнал, минус оператор платежей мерчанта. */
const EXPORT_ROLES = [ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER]

/**
 * Ширина кнопки фиксирована по самой длинной подписи: без этого смена текста на «Формируется
 * файл...» дёргает соседние кнопки фильтра.
 */
const BUTTON_MIN_WIDTH = 190

interface Props {
  /** Применённый фильтр журнала — файл собирается ровно по нему. */
  query: OverpaymentFilters
}

/**
 * Кнопка выгрузки журнала переоплат — отдаётся в проп `actions` у `FilterManager`, поэтому
 * встаёт под «Применить» / «Сбросить фильтр». Роль без доступа кнопки не видит.
 */
const ExportButton: React.FC<Props> = ({ query }) => {
  const { currentUser } = useAuth()
  const [isExporting, setIsExporting] = useState(false)

  // Выгрузка идёт по применённому фильтру и без страницы — бэкенд отдаёт всю выборку одним файлом,
  // поэтому она заметно дольше загрузки страницы журнала. На это время кнопка гасится и говорит,
  // что занята: браузерный диалог сохранения появляется только в самом конце, и без состояния
  // ожидания клик выглядит как «ничего не произошло», а повторные нажатия собирают файл заново.
  const handleExport = async () => {
    setIsExporting(true)

    try {
      const { data } = await exportOverpaymentList(query)

      if (data instanceof Blob) {
        fileDownload(data, `overpayments-${format(new Date(), 'dd-MM-yyyy_HH-mm')}.xlsx`)
      }
    } catch {
      AppSwal.fire({
        title: 'Не удалось выгрузить журнал',
        text: 'Попробуйте повторить выгрузку. Если ошибка повторяется — сузьте период фильтром.',
        icon: 'error',
      })
    } finally {
      setIsExporting(false)
    }
  }

  if (!IsGranted(EXPORT_ROLES, currentUser)) {
    return null
  }

  return (
    <button
      type='button'
      className='btn btn-primary'
      style={{ minWidth: BUTTON_MIN_WIDTH }}
      onClick={handleExport}
      disabled={isExporting}
    >
      {isExporting ? (
        <span className='d-flex align-items-center justify-content-center'>
          <span className='spinner-border spinner-border-sm align-middle me-2' />
          Формируется файл...
        </span>
      ) : (
        'Экспортировать'
      )}
    </button>
  )
}

export default ExportButton
